/**
 * Admin Onboarding — Verify Email Landing
 * =========================================
 *
 * TREATX-ADMIN-ONBOARDING-01 · frontend
 *
 * Deep-link target for the verification email. Reads the `token`
 * query parameter, POSTs it to the public
 * `/api/admin/onboarding/verify-email` endpoint, and displays one
 * of the four safe states: `verifying`, `success`, `invalid_or_expired`
 * (which covers already-used, expired, tampered, and unknown), or
 * `missing_token`.
 *
 * On success we DO NOT keep the token in the URL any longer than
 * necessary — the browser is navigated away with `replace` so a back
 * button does not carry the token forward. On failure the same
 * opaque error is surfaced regardless of which server-side branch
 * fired, matching the backend contract.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, XCircle, Loader2, AlertTriangle, LogIn } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';

const BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

function readTokenFromHash() {
  // Route is `/#/admin/onboarding/verify-email?token=...`.  Depending
  // on how the router serialises the hash, the query may live after
  // the hash or on window.location.search. Cover both.
  try {
    const hash = window.location.hash || '';
    const q = hash.indexOf('?');
    if (q >= 0) {
      const params = new URLSearchParams(hash.slice(q + 1));
      const t = params.get('token');
      if (t) return t;
    }
    const search = window.location.search || '';
    if (search) {
      const params = new URLSearchParams(search);
      const t = params.get('token');
      if (t) return t;
    }
  } catch {
    /* ignore */
  }
  return '';
}

export default function AdminOnboardingVerifyEmail() {
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useState('verifying');   // verifying | success | invalid | missing
  // Track the token WE consumed so a hash-only URL change (same
  // component instance) re-runs the verification against the new
  // token instead of replaying the last outcome.
  const lastConsumed = useRef(null);

  useEffect(() => {
    const token = readTokenFromHash();
    if (!token) {
      setState('missing');
      return;
    }
    if (lastConsumed.current === token) {
      return; // already resolved for this token in this mount
    }
    lastConsumed.current = token;
    setState('verifying');
    (async () => {
      try {
        // Public endpoint — no auth headers. Deliberately does NOT
        // reuse the api util so we cannot leak a stale JWT cookie.
        await axios.post(
          `${BASE}/api/admin/onboarding/verify-email`,
          { token },
          { headers: { 'Content-Type': 'application/json' } },
        );
        setState('success');
        // Scrub the token from the URL bar as soon as verification lands.
        try {
          window.history.replaceState(
            {}, '', window.location.pathname + '#/admin/onboarding/verify-email',
          );
        } catch { /* ignore */ }
      } catch {
        // Opaque failure — same UI whether replayed, expired, or bogus.
        setState('invalid');
      }
    })();
    // Re-evaluate whenever the URL hash / search changes so a replay
    // navigation is handled correctly.
  }, [location.hash, location.search, location.pathname]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4"
         data-testid="admin-onboarding-verify-landing">
      <Card className="w-full max-w-md shadow-lg border border-slate-200">
        <CardHeader className="text-center pb-2">
          {state === 'verifying' && (
            <>
              <div className="mx-auto w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-3">
                <Loader2 className="h-6 w-6 text-slate-500 animate-spin" />
              </div>
              <CardTitle className="text-slate-800 font-[Manrope] text-xl">
                Verifying your email…
              </CardTitle>
              <p className="text-sm text-slate-500 mt-1">
                One moment while we confirm the link.
              </p>
            </>
          )}
          {state === 'success' && (
            <>
              <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mb-3">
                <CheckCircle2 className="h-6 w-6 text-emerald-600" />
              </div>
              <CardTitle className="text-slate-800 font-[Manrope] text-xl">
                Email verified
              </CardTitle>
              <p className="text-sm text-slate-500 mt-1">
                Onboarding complete. Sign in to reach your admin workspace.
              </p>
            </>
          )}
          {state === 'invalid' && (
            <>
              <div className="mx-auto w-12 h-12 rounded-full bg-rose-100 flex items-center justify-center mb-3">
                <XCircle className="h-6 w-6 text-rose-600" />
              </div>
              <CardTitle className="text-slate-800 font-[Manrope] text-xl">
                Link no longer valid
              </CardTitle>
              <p className="text-sm text-slate-500 mt-1">
                This verification link may have already been used, expired, or
                been tampered with. Request a fresh one after signing in.
              </p>
            </>
          )}
          {state === 'missing' && (
            <>
              <div className="mx-auto w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mb-3">
                <AlertTriangle className="h-6 w-6 text-amber-600" />
              </div>
              <CardTitle className="text-slate-800 font-[Manrope] text-xl">
                No verification token
              </CardTitle>
              <p className="text-sm text-slate-500 mt-1">
                Open the link from your verification email — the token in the
                URL is required.
              </p>
            </>
          )}
        </CardHeader>
        <CardContent className="space-y-2">
          {state === 'success' && (
            <Button
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={() => navigate('/login', { replace: true })}
              data-testid="onboarding-goto-login"
            >
              <LogIn className="h-4 w-4 mr-2" /> Continue to sign in
            </Button>
          )}
          {(state === 'invalid' || state === 'missing') && (
            <Button
              className="w-full"
              variant="outline"
              onClick={() => navigate('/login', { replace: true })}
              data-testid="onboarding-back-to-login"
            >
              <LogIn className="h-4 w-4 mr-2" /> Back to sign in
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
